import { Button, createTableColumn, makeStyles, TableCellLayout, TableColumnDefinition } from '@fluentui/react-components';
import React from 'react';
import { Outlet } from 'react-router-dom';
import { Therm } from '../model/Therm';
import { ItemsListProps, ItemsListWithoutStyles, ItemsListWithStyles } from './ItemsList';
import { SiteHeader } from './SiteHeader';
import { withStyles } from "./withStyles";
import { Smoke } from "../model/Smoke";
import { AddOrUpdateDelegate, DeleteDelegate } from "../model/ItemDelegates";
import { EditRegular, DeleteRegular } from "@fluentui/react-icons";
import { EditAddTherm } from '../dialogs/EditAddTherm';
import { ModalPrompt } from '../dialogs/ModalPrompt';

export interface ThermsListProps
{
    styles: Record<string, string>;
    therms: Therm[];
    smoke: Smoke;
    onDelete?: DeleteDelegate<Therm>;
    onCommit?: AddOrUpdateDelegate<Therm>;
}

export interface ThermsListState
{
    isEditing: boolean;
    isAdding: boolean;
    itemEditing?: Therm;
    isConfirmingDelete: boolean;
    itemDeleting?: Therm;
}

interface ThermsListItemArgs
{
}

const useStyles = makeStyles(
    {
    });

const ThermsListItemsList: React.ComponentType<ItemsListProps<Therm, ThermsListItemArgs>> =
    ItemsListWithStyles<Therm, ThermsListItemArgs>(ItemsListWithoutStyles < Therm, ThermsListItemArgs > );

export class ThermsListWithoutStyles extends React.Component<ThermsListProps, ThermsListState>
{
    constructor(props: ThermsListProps)
    {
        super(props);
        this.state =
        {
            isEditing: false,
            isAdding: false,
            itemEditing: new Therm(),
            isConfirmingDelete: false,
            itemDeleting: undefined
        };
    }

    startEdit(item: Therm)
    {
        this.setState(
            {
                isEditing: true,
                isAdding: false,
                itemEditing: item
            });
    }

    onEditButtonClick(event: React.MouseEvent<HTMLButtonElement>, item: Therm)
    {
        this.startEdit(item);
        event.stopPropagation();
    }

    startAdd()
    {
        const newItem = new Therm();
        newItem.SmokeID = this.props.smoke.SmokeID;

        this.setState(
            {
                isEditing: true,
                isAdding: true,
                itemEditing: newItem
            });
    }

    onDeleteButtonClick(event: React.MouseEvent<HTMLButtonElement>, item: Therm)
    {
        this.setState(
            {
                isConfirmingDelete: true,
                itemDeleting: item
            });
        event.stopPropagation();
    }

    async onDeleteConfirmed()
    {
        if (!this.props.onDelete)
            throw new Error("onDelete not set");

        await this.props.onDelete(this.state.itemDeleting!);

        this.setState(this.stateResetDialogs());
    }

    async onSaveEditDialog(item: Therm)
    {
        if (!this.props.onCommit)
            throw new Error("onCommit not set");

        await this.props.onCommit(item, this.state.isAdding);
        this.setState(this.stateResetDialogs());
    }

    stateResetDialogs()
    {
        return {
            isEditing: false,
            isAdding: false,
            isConfirmingDelete: false,
            itemEditing: undefined,
            itemDeleting: undefined
        };
    }

    onDismissDialogs()
    {
        this.setState(this.stateResetDialogs());
    }

    getTableColumnDefinitions()
    {
        const columns: TableColumnDefinition<Therm>[] =
        [
            createTableColumn<Therm>(
                {
                    columnId: "thermId",
                    compare: (left, right) => left.ThermID.localeCompare(right.ThermID),
                    renderHeaderCell: () => { return "ID"; },
                    renderCell: (item: Therm) =>
                    {
                        return (
                            <TableCellLayout>{item.ThermID}</TableCellLayout>);
                    }
                }),
            createTableColumn<Therm>(
                {
                    columnId: "description",
                    compare: (left, right) => left.Description.localeCompare(right.Description),
                    renderHeaderCell: () => { return "Description"; },
                    renderCell: (item: Therm) =>
                    {
                        return (
                            <TableCellLayout>{item.Description}</TableCellLayout>);
                    }
                }),
            createTableColumn<Therm>(
                {
                    columnId: "actions",
                    renderHeaderCell: () => { return "Actions"; },
                    renderCell: (item: Therm) =>
                    {
                        return (
                            <span>
                                <Button aria-label="Edit" icon={<EditRegular/>} onClick={(event) => this.onEditButtonClick(event, item)}/>
                                <Button aria-label="Delete" icon={<DeleteRegular/>} onClick={(event) => this.onDeleteButtonClick(event, item)}/>
                            </span>
                        );
                    }
                }),
        ];

        return columns;
    }

    render()
    {
        const getRowId = (item: Therm) => `${item.ThermID}`;
        const onEditButtonClicked = (item: Therm, args: ThermsListItemArgs) => {}
        const columns = this.getTableColumnDefinitions();
        const canEditItem = (item: Therm) => true;
        const onDismiss = this.onDismissDialogs.bind(this);
        const onDeleteConfirmed = this.onDeleteConfirmed.bind(this);

        const editAddDialog = (
            <EditAddTherm
                open={this.state.isEditing}
                itemEditing={this.state.itemEditing}
                isAdd={this.state.isAdding}
                visible={false}
                onDismiss={onDismiss}
                onSave={this.onSaveEditDialog.bind(this)}/>);

        const confirmDeleteDialog = (
            <ModalPrompt title="Delete Therm?" confirm="Delete" onDismiss={onDismiss} onSave={onDeleteConfirmed} open={this.state.isConfirmingDelete} visible={false}>
                Are you sure you want to delete {this.state.itemDeleting?.Description} for smoke {this.props.smoke?.Description}?
            </ModalPrompt>);

        return (
            <div>
                <ThermsListItemsList
                    editArgs={{}}
                    getRowId={getRowId}
                    items={this.props.therms}
                    columns={columns}
                    canEditItem={canEditItem}
                    onEditItem={onEditButtonClicked}/>

                <Button onClick={() => this.startAdd()}>Add</Button>

                {this.state.isEditing && editAddDialog}
                {this.state.isConfirmingDelete && confirmDeleteDialog}
            </div>
        );
    }
}

export const ThermsList = withStyles(useStyles, ThermsListWithoutStyles);